import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { toast } from "sonner";

import { acceptCashout } from "@/api/coupons";
// The leaf file, not the "@/tanstackQuery/queries" barrel: the list below is
// built when this module loads, and through the barrel the import cycle
// (queries -> ... -> this mutation -> queries) leaves CouponsQueryKeys
// undefined at that moment, which crashed every page that loads it.
import { CouponsQueryKeys } from "@/tanstackQuery/queries/coupons/couponsQueryKeys";
import { getChangedCashoutOffer } from "@/utils/cashout";
import { currencySymbol } from "@/utils/currency";
import { formatNumber } from "@/utils/format";
import { notifyMutationError } from "@/utils/mutationError";

// Everything a cashed-out coupon changes: its status in every coupon list,
// the statements, and the offers. The balance arrives on the existing
// `updateBalance` socket event, as with a cancel.
const CASHOUT_INVALIDATED_KEYS = [
  CouponsQueryKeys.History,
  CouponsQueryKeys.UserHistory,
  CouponsQueryKeys.UserHistoryInfinite,
  CouponsQueryKeys.Single,
  CouponsQueryKeys.UserSingle,
  CouponsQueryKeys.HistoryPaginated,
  CouponsQueryKeys.Keyset,
  CouponsQueryKeys.Statement,
  CouponsQueryKeys.CashoutOffers,
];

/**
 * Accepts a cash-out offer exactly as it was shown. Never retried (the
 * mutation default) and never auto-accepted: a CASHOUT_OFFER_CHANGED refusal
 * carries a new, lower offer that the caller shows and asks about again, so
 * it raises no error toast here. Every other failure uses the shared toast.
 */
export const useAcceptCashout = () => {
  const queryClient = useQueryClient();
  const t = useTranslations();

  return useMutation({
    mutationFn: acceptCashout,
    onSuccess: (data) => {
      toast.success(
        t("bet_history.cashout.success", {
          amount: `${currencySymbol}${formatNumber(data.amount)}`,
        }),
        {
          duration: 3000,
          style: {
            backgroundColor: "var(--color-accent-vibrant)",
            color: "var(--color-text-inverse)",
          },
        }
      );
      for (const key of CASHOUT_INVALIDATED_KEYS) {
        queryClient.invalidateQueries({ queryKey: [key] });
      }
    },
    onError: (error) => {
      queryClient.invalidateQueries({
        queryKey: [CouponsQueryKeys.CashoutOffers],
      });
      if (getChangedCashoutOffer(error)) return;
      notifyMutationError(t, error);
    },
  });
};
